import type { PaymentMethod } from '@/services/api/api.types'

/**
 * Checks if a payment method has changed by comparing key fields
 * @param previous - The previous payment method (can be null for new additions)
 * @param current - The current payment method (can be null for removals)
 * @returns true if the payment method has been added, updated, or removed
 */
export function hasPaymentMethodChanged(previous: PaymentMethod | null, current: PaymentMethod | null): boolean {
  // Payment method was added (previously null, now exists)
  if (!previous && current) {
    return true
  }

  // Payment method was removed (previously existed, now null)
  if (previous && !current) {
    return true
  }

  // Both null - no change
  if (!previous && !current) {
    return false
  }

  if (previous && current) {
    // First check if payment method type changed
    if (previous.type !== current.type) {
      return true
    }

    // If both are card types, compare card-specific fields
    if (previous.type === 'card' && current.type === 'card') {
      return (
        previous.last4 !== current.last4 ||
        previous.brand !== current.brand ||
        previous.expMonth !== current.expMonth ||
        previous.expYear !== current.expYear
      )
    }

    // For non-card payment methods, type comparison is sufficient
    // since we don't have other comparable fields for bank accounts, etc.
    return false
  }

  return false
}

/**
 * Checks if a payment method was added (previously null, now exists)
 */
export function wasPaymentMethodAdded(previous: PaymentMethod | null, current: PaymentMethod | null): boolean {
  return !previous && !!current
}

/**
 * Checks if a payment method was removed (previously existed, now null)
 */
export function wasPaymentMethodRemoved(previous: PaymentMethod | null, current: PaymentMethod | null): boolean {
  return !!previous && !current
}

/**
 * Checks if payment method details were updated (both exist but fields changed)
 */
export function wasPaymentMethodUpdated(previous: PaymentMethod | null, current: PaymentMethod | null): boolean {
  if (!previous || !current) {
    return false // Can't be an update if either is null
  }

  return (
    previous.last4 !== current.last4 ||
    previous.brand !== current.brand ||
    previous.expMonth !== current.expMonth ||
    previous.expYear !== current.expYear
  )
}

/**
 * Generates an appropriate payment method description for billing displays
 * @param paymentMethod - The payment method object
 * @returns A user-friendly description of the payment method
 */
export function getPaymentMethodDescription(paymentMethod: PaymentMethod): string {
  // For card-based payment methods with last4 digits
  if (paymentMethod.last4) {
    return `card ending in ${paymentMethod.last4}`
  }

  // For other payment method types, use the type name with proper formatting
  const formattedType = paymentMethod.type.charAt(0).toUpperCase() + paymentMethod.type.slice(1)
  return formattedType.replace('_', ' ') // Convert snake_case to readable format
}
